import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { ticketGet } from '../../services/apiClient';
import { userManagementService } from '../../api/userManagementService';
import { extractErrorMessage } from '../../utils/errorHandler';
import { excludeSuperAdminFromReportRows } from '../../utils/reportFilters';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import CountyFilterSelect, { countyFilterToId, regionFilterToId } from '../../components/reports/CountyFilterSelect';
import { downloadStyledXlsx } from '../../utils/exportStyledXlsx';
import { 
  Users, 
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  Download,
  ChevronDown,
  FileText
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

const AgentsReport = () => {
  const [filteredAgentsData, setFilteredAgentsData] = useState([]);
  const [chartStats, setChartStats] = useState({ byStatus: [], byAgent: [] });
  const [loading, setLoading] = useState(false);
  const [chartLoading, setChartLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Filter states
  const [selectedAgent, setSelectedAgent] = useState('all');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedCounty, setSelectedCounty] = useState('all');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [paginationData, setPaginationData] = useState(null);
  
  const [exporting, setExporting] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  // Dropdown data
  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);

  // Load agents on mount
  useEffect(() => {
    loadAgents();
  }, []);

  // Charts: refresh on filter change only
  useEffect(() => {
    loadAgentReportStatistics();
  }, [selectedAgent, selectedRegion, selectedCounty]);

  // Table: refresh on filter or pagination change
  useEffect(() => {
    loadAgentPerformanceTable();
  }, [currentPage, pageSize, selectedAgent, selectedRegion, selectedCounty]);

  const buildFilterParams = () => {
    const params = {};
    if (selectedAgent !== 'all') {
      const agentId = parseInt(selectedAgent, 10);
      if (!isNaN(agentId)) params.agentId = agentId;
    }
    const regionId = regionFilterToId(selectedRegion);
    if (regionId !== null) params.regionId = regionId;
    const countyId = countyFilterToId(selectedCounty);
    if (countyId !== null) params.countyId = countyId;
    return params;
  };

  const loadAgents = async () => {
    try {
      setLoadingAgents(true);
      const response = await userManagementService.getAgents({ size: 1000 }); // Get all agents
      const agentsData = response?.data?.data?.content || response?.data?.data || response?.data || [];
      const agentsArray = Array.isArray(agentsData) ? agentsData : [];
      setAgents(agentsArray);
    } catch (error) {
      console.warn('Failed to load agents:', error);
      setAgents([]);
    } finally {
      setLoadingAgents(false);
    }
  };

  const loadAgentReportStatistics = async () => {
    try {
      setChartLoading(true);
      const response = await ticketGet('/gateway/api/ticket/analytics/agent-performance/statistics', buildFilterParams());
      const data = response?.data?.data || response?.data || {};
      setChartStats({
        byStatus: Array.isArray(data.byStatus) ? data.byStatus : [],
        byAgent: Array.isArray(data.byAgent) ? data.byAgent : []
      });
    } catch (err) {
      console.warn('Agent report statistics load failed:', err);
      setChartStats({ byStatus: [], byAgent: [] });
    } finally {
      setChartLoading(false);
    }
  };

  const loadAgentPerformanceTable = async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        ...buildFilterParams(),
        page: currentPage,
        size: pageSize,
        sortField: 'totalCount',
        sortDirection: 'desc'
      };

      const response = await ticketGet('/gateway/api/ticket/analytics/agent-performance', params);

      let pagePayload = null;
      if (response?.success && response?.data) {
        pagePayload = response.data;
      } else if (response?.data?.success && response?.data?.data) {
        pagePayload = response.data.data;
      } else if (response?.data) {
        pagePayload = response.data;
      }

      const content = Array.isArray(pagePayload?.content)
        ? pagePayload.content
        : (Array.isArray(pagePayload) ? pagePayload : []);

      const rows = content.map((item) => ({
        ...item,
        assigneeName: item.key || item.assigneeName || item.agentName || 'Unknown'
      }));

      setFilteredAgentsData(rows);
      setPaginationData({
        totalElements: pagePayload?.totalElements ?? rows.length,
        totalPages: pagePayload?.totalPages ?? Math.ceil((pagePayload?.totalElements ?? rows.length) / pageSize),
        pageNumber: pagePayload?.pageNumber ?? currentPage,
        pageSize: pagePayload?.pageSize ?? pageSize,
        first: pagePayload?.first ?? currentPage === 0,
        last: pagePayload?.last ?? true
      });
    } catch (error) {
      console.error('Error loading agents report:', error);
      setError(extractErrorMessage(error, 'Failed to load agents report'));
      toast.error(extractErrorMessage(error, 'Failed to load agents report'));
      setFilteredAgentsData([]);
      setPaginationData(null);
    } finally {
      setLoading(false);
    }
  };

  const refreshAll = () => {
    loadAgentReportStatistics();
    loadAgentPerformanceTable();
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && (paginationData ? newPage < paginationData.totalPages : true)) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleClearFilters = () => {
    setSelectedAgent('all');
    setSelectedRegion('all');
    setSelectedCounty('all');
    setCurrentPage(0);
  };

  const hasActiveFilters = selectedAgent !== 'all' || selectedRegion !== 'all' || selectedCounty !== 'all';

  const pieChartData = React.useMemo(() => {
    return (chartStats.byStatus || [])
      .map((item) => ({
        name: String(item.name || 'Unknown').trim() || 'Unknown',
        value: Number(item.count) || 0
      }))
      .filter((d) => d.value > 0);
  }, [chartStats]);

  const barChartData = React.useMemo(() => {
    return (chartStats.byAgent || []).map((a) => {
      const fullName = a.assigneeName || 'Unknown';
      return {
        name: String(fullName).substring(0, 18),
        totalTickets: Number(a.totalTickets) || 0,
        fullName
      };
    });
  }, [chartStats]);

  const CHART_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

  const fetchAgentsReportForExport = async () => {
    const params = { type: 'AGENT_PERFORMANCE' };
    if (selectedAgent !== 'all') {
      const agentId = parseInt(selectedAgent, 10);
      if (!isNaN(agentId)) params.agentId = agentId;
    }
    const regionId = regionFilterToId(selectedRegion);
    if (regionId !== null) params.regionId = regionId;
    const countyId = countyFilterToId(selectedCounty);
    if (countyId !== null) params.countyId = countyId;
    const response = await ticketGet('/gateway/api/ticket/analytics/report', params);
    let reportData = [];
    if (response?.success && response?.data) {
      reportData = Array.isArray(response.data) ? response.data : [];
    } else if (response?.data?.success && response?.data?.data) {
      reportData = Array.isArray(response.data.data) ? response.data.data : [];
    } else if (response?.data) {
      reportData = Array.isArray(response.data) ? response.data : [];
    } else if (Array.isArray(response)) {
      reportData = response;
    }
    return excludeSuperAdminFromReportRows(
      reportData.map((item) => ({
        ...item,
        assigneeName: item.key || item.assigneeName || item.agentName || 'Unknown'
      }))
    );
  };

  const handleExportExcel = async () => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      const exportData = await fetchAgentsReportForExport();
      if (exportData.length === 0) {
        toast.error('No data to export');
        return;
      }
      const headers = [
        'Agent Name',
        'Total Tickets',
        'Open',
        'Re-Open',
        'In Progress',
        'Resolved',
        'Closed',
        'Escalated',
        'Surveys Received',
        '% Very Satisfied',
        '% Satisfied',
        '% Neutral',
        '% Dissatisfied',
        'CSAT %',
      ];
      const rows = exportData.map((r) => [
        r.assigneeName,
        r.totalCount ?? 0,
        r.openCount ?? 0,
        r.reopenCount ?? 0,
        r.inProgressCount ?? 0,
        r.resolvedCount ?? 0,
        r.closedCount ?? 0,
        r.escalatedCount ?? 0,
        r.surveysReceived ?? 0,
        r.percentVerySatisfied != null ? `${r.percentVerySatisfied.toFixed(1)}%` : '0.0%',
        r.percentSatisfied != null ? `${r.percentSatisfied.toFixed(1)}%` : '0.0%',
        r.percentNeutral != null ? `${r.percentNeutral.toFixed(1)}%` : '0.0%',
        r.percentDissatisfied != null ? `${r.percentDissatisfied.toFixed(1)}%` : '0.0%',
        r.csatPercentage != null ? `${r.csatPercentage.toFixed(1)}%` : '0.0%',
      ]);
      downloadStyledXlsx({
        fileName: `agents-report-${new Date().toISOString().slice(0, 10)}.xlsx`,
        sheetName: 'Agents Report',
        headers,
        rows,
        numberColumns: [1, 2, 3, 4, 5, 6, 7, 8],
        textColumns: [0, 9, 10, 11, 12, 13],
        centerColumns: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13],
      });
      toast.success('Report exported as Excel');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      const exportData = await fetchAgentsReportForExport();
      if (exportData.length === 0) {
        toast.error('No data to export');
        return;
      }
      const doc = new jsPDF({ orientation: 'landscape' });
      doc.setFontSize(14);
      doc.text('Agents Report', 14, 15);
      doc.setFontSize(10);
      const head = [['Agent Name', 'Total', 'Open', 'Re-Open', 'In Progress', 'Resolved', 'Closed', 'Escalated', 'Surveys', '% Very Sat', '% Sat', '% Neutral', '% Dissat', 'CSAT %']];
      const body = exportData.map((r) => [
        (r.assigneeName || '').substring(0, 20),
        r.totalCount ?? 0,
        r.openCount ?? 0,
        r.reopenCount ?? 0,
        r.inProgressCount ?? 0,
        r.resolvedCount ?? 0,
        r.closedCount ?? 0,
        r.escalatedCount ?? 0,
        r.surveysReceived ?? 0,
        r.percentVerySatisfied != null ? `${r.percentVerySatisfied.toFixed(1)}%` : '0.0%',
        r.percentSatisfied != null ? `${r.percentSatisfied.toFixed(1)}%` : '0.0%',
        r.percentNeutral != null ? `${r.percentNeutral.toFixed(1)}%` : '0.0%',
        r.percentDissatisfied != null ? `${r.percentDissatisfied.toFixed(1)}%` : '0.0%',
        r.csatPercentage != null ? `${r.csatPercentage.toFixed(1)}%` : '0.0%'
      ]);
      autoTable(doc, { startY: 22, head, body, styles: { fontSize: 7 } });
      doc.save(`agents-report-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success('Report exported as PDF');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Agents Report</h1>
          <p className="text-sm text-muted-foreground mt-1">
            View agent performance metrics and ticket statistics
          </p>
        </div>
        <div className="flex flex-nowrap items-center justify-end gap-2">
          <Select
            value={selectedAgent}
            onValueChange={(value) => {
              setSelectedAgent(value);
              setCurrentPage(0);
            }}
            disabled={loadingAgents}
          >
            <SelectTrigger className="w-[200px] h-9 rounded-xl border border-primary bg-white text-[#000000] hover:bg-[#E0F8F3] font-medium text-sm focus:ring-ring focus:border-primary">
              <SelectValue placeholder="All Agents" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Agents</SelectItem>
              {agents.map((agent) => (
                <SelectItem
                  key={agent.userId || agent.id}
                  value={String(agent.userId || agent.id)}
                >
                  {agent.fullName || agent.name || agent.username || 'Unknown'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <CountyFilterSelect
            variant="button"
            className="shrink-0"
            regionValue={selectedRegion}
            countyValue={selectedCounty}
            onRegionChange={(value) => {
              setSelectedRegion(value);
              setCurrentPage(0);
            }}
            onCountyChange={(value) => {
              setSelectedCounty(value);
              setCurrentPage(0);
            }}
          />
          <div className="relative shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setExportMenuOpen((v) => !v)}
              disabled={loading || exporting}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              {exporting ? 'Exporting…' : 'Export'}
              <ChevronDown className="h-4 w-4 ml-1" />
            </Button>
            {exportMenuOpen && (
              <div className="absolute right-0 top-full mt-1 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-10 min-w-[140px]">
                <button type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2" onClick={handleExportExcel}>
                  <FileText className="h-4 w-4" /> Export Excel
                </button>
                <button type="button" className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2" onClick={handleExportPdf}>
                  <FileText className="h-4 w-4" /> Export PDF
                </button>
              </div>
            )}
          </div>
          <Button
            onClick={refreshAll}
            disabled={loading || chartLoading}
            variant="outline"
            size="sm"
            className="flex items-center gap-2 shrink-0"
          >
            <RefreshCw className={`h-4 w-4 ${loading || chartLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleClearFilters}
            disabled={loading || !hasActiveFilters}
            className="h-9 shrink-0"
          >
            Clear Filters
          </Button>
        </div>
      </div>

      {/* Charts: Pie (status distribution) and Bar (tickets per agent) */}
      {(chartLoading || chartStats.byStatus.length > 0 || chartStats.byAgent.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <BarChart3 className="h-5 w-5" />
                Ticket count by status
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Distribution across all agents in this report
              </p>
            </CardHeader>
            <CardContent>
              {chartLoading ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <RefreshCw className="h-8 w-8 animate-spin mb-3" />
                  <p className="text-sm">Loading chart...</p>
                </div>
              ) : pieChartData.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No ticket counts to display</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={pieChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {pieChartData.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [value, 'Count']}
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Users className="h-5 w-5" />
                Total tickets per agent
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Agent performance report count
              </p>
            </CardHeader>
            <CardContent>
              {chartLoading ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <RefreshCw className="h-8 w-8 animate-spin mb-3" />
                  <p className="text-sm">Loading chart...</p>
                </div>
              ) : barChartData.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No ticket counts to display</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart
                    data={barChartData}
                    margin={{ top: 8, right: 8, left: 8, bottom: 24 }}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis type="number" allowDecimals={false} className="text-xs" />
                    <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
                    <Tooltip
                      formatter={(value) => [value, 'Total tickets']}
                      labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName ?? ''}
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar dataKey="totalTickets" fill={CHART_COLORS[0]} name="Total tickets" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Agents Report Table Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Agent Performance
            {paginationData && (
              <span className="text-sm font-normal text-muted-foreground ml-2">
                ({paginationData.totalElements} total)
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-800 text-sm">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Loading agents report...</span>
            </div>
          ) : filteredAgentsData.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No agent data found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-border bg-muted/50">
                      <th className="text-left p-3 text-sm font-semibold text-foreground">Agent Name</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground">Total Tickets</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground">Open</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground">Re-Open</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground">In Progress</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground">Resolved</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground">Closed</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground">Escalated</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground">Surveys Received</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground">% Very Satisfied</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground">% Satisfied</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground">% Neutral</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground">% Dissatisfied</th>
                      <th className="text-left p-3 text-sm font-semibold text-foreground">CSAT %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAgentsData.map((agentData, index) => (
                      <tr
                        key={agentData.assigneeId || agentData.agentId || agentData.userId || agentData.id || index}
                        className="border-b border-border hover:bg-muted/30 transition-colors"
                      >
                        <td className="p-3 text-sm text-foreground font-medium">
                          {agentData.assigneeName}
                        </td>
                        <td className="p-3 text-sm text-foreground">
                          {agentData.totalCount || 0}
                        </td>
                        <td className="p-3 text-sm text-foreground">
                          {agentData.openCount || 0}
                        </td>
                        <td className="p-3 text-sm text-foreground">
                          {agentData.reopenCount || 0}
                        </td>
                        <td className="p-3 text-sm text-foreground">
                          {agentData.inProgressCount || 0}
                        </td>
                        <td className="p-3 text-sm text-foreground">
                          {agentData.resolvedCount || 0}
                        </td>
                        <td className="p-3 text-sm text-foreground">
                          {agentData.closedCount || 0}
                        </td>
                        <td className="p-3 text-sm text-foreground">
                          {agentData.escalatedCount || 0}
                        </td>
                        <td className="p-3 text-sm text-foreground">
                          {agentData.surveysReceived || 0}
                        </td>
                        <td className="p-3 text-sm text-foreground">
                          {agentData.percentVerySatisfied != null 
                            ? `${agentData.percentVerySatisfied.toFixed(1)}%` 
                            : '0.0%'}
                        </td>
                        <td className="p-3 text-sm text-foreground">
                          {agentData.percentSatisfied != null 
                            ? `${agentData.percentSatisfied.toFixed(1)}%` 
                            : '0.0%'}
                        </td>
                        <td className="p-3 text-sm text-foreground">
                          {agentData.percentNeutral != null 
                            ? `${agentData.percentNeutral.toFixed(1)}%` 
                            : '0.0%'}
                        </td>
                        <td className="p-3 text-sm text-foreground">
                          {agentData.percentDissatisfied != null 
                            ? `${agentData.percentDissatisfied.toFixed(1)}%` 
                            : '0.0%'}
                        </td>
                        <td className="p-3 text-sm text-foreground font-medium">
                          {agentData.csatPercentage != null 
                            ? `${agentData.csatPercentage.toFixed(1)}%` 
                            : '0.0%'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {paginationData && paginationData.totalElements > 0 && (
                <div className="flex flex-col sm:flex-row items-center justify-between mt-6 pt-4 border-t border-border gap-4">
                  <div className="text-sm text-muted-foreground">
                    Showing {currentPage * pageSize + 1} to{' '}
                    {Math.min((currentPage + 1) * pageSize, paginationData.totalElements)} of{' '}
                    {paginationData.totalElements} agents
                  </div>
                  <div className="flex items-center gap-2">
                    {/* First Page Button */}
                    {paginationData.totalPages > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(0)}
                        disabled={currentPage === 0 || loading}
                        className="hidden sm:flex"
                      >
                        First
                      </Button>
                    )}
                    
                    {/* Previous Button */}
                    {paginationData.totalPages > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 0 || loading}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Previous
                      </Button>
                    )}
                    
                    {/* Page Numbers */}
                    {paginationData.totalPages > 0 && (
                      <div className="flex items-center gap-1">
                        {(() => {
                          const pages = [];
                          const totalPages = paginationData.totalPages;
                          const current = currentPage + 1;
                          
                          if (totalPages <= 7) {
                            for (let i = 1; i <= totalPages; i++) {
                              pages.push(
                                <Button
                                  key={i}
                                  variant={i === current ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => handlePageChange(i - 1)}
                                  disabled={loading || totalPages === 1}
                                  className="min-w-[2.5rem]"
                                >
                                  {i}
                                </Button>
                              );
                            }
                          } else {
                            pages.push(
                              <Button
                                key={1}
                                variant={current === 1 ? "default" : "outline"}
                                size="sm"
                                onClick={() => handlePageChange(0)}
                                disabled={loading}
                                className="min-w-[2.5rem]"
                              >
                                1
                              </Button>
                            );
                            
                            if (current > 3) {
                              pages.push(
                                <span key="ellipsis-start" className="px-2 text-muted-foreground">
                                  ...
                                </span>
                              );
                            }
                            
                            const start = Math.max(2, current - 1);
                            const end = Math.min(totalPages - 1, current + 1);
                            
                            for (let i = start; i <= end; i++) {
                              if (i !== 1 && i !== totalPages) {
                                pages.push(
                                  <Button
                                    key={i}
                                    variant={i === current ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => handlePageChange(i - 1)}
                                    disabled={loading}
                                    className="min-w-[2.5rem]"
                                  >
                                    {i}
                                  </Button>
                                );
                              }
                            }
                            
                            if (current < totalPages - 2) {
                              pages.push(
                                <span key="ellipsis-end" className="px-2 text-muted-foreground">
                                  ...
                                </span>
                              );
                            }
                            
                            pages.push(
                              <Button
                                key={totalPages}
                                variant={current === totalPages ? "default" : "outline"}
                                size="sm"
                                onClick={() => handlePageChange(totalPages - 1)}
                                disabled={loading}
                                className="min-w-[2.5rem]"
                              >
                                {totalPages}
                              </Button>
                            );
                          }
                          
                          return pages;
                        })()}
                      </div>
                    )}
                    
                    {/* Next Button */}
                    {paginationData.totalPages > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage >= paginationData.totalPages - 1 || loading}
                      >
                        Next
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    )}
                    
                    {/* Last Page Button */}
                    {paginationData.totalPages > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(paginationData.totalPages - 1)}
                        disabled={currentPage >= paginationData.totalPages - 1 || loading}
                        className="hidden sm:flex"
                      >
                        Last
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AgentsReport;
